"use client";

import React, { useState, useEffect, useCallback, useRef } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { ArrowLeft, Save, Loader2, MessageSquare } from "lucide-react";
import { Badge } from "../ui/badge";
import { Label } from "../ui/label";
import { DocumentManager } from "../planning/DocumentManager";
import { getCompanyName } from "@/lib/textron-data-processor";
import { useArchivalMode } from "@/contexts/ArchivalModeContext";
import { getFY_YEAR } from "@/utils/sharepoint-env";
import { ReviewComment } from "../shared/ReviewComment";

interface CustomProcedureProps {
  workspaceName: string;
  procedureName: string;
  workspacePrefix: string;
  area: "Planning" | "Execution" | "ConcludingProcedures";
  onBack?: () => void;
}

interface QuestionResponse {
  textResponse?: { html: string; plainText: string } | null;
  documents?: Array<{ id: string; type: string; name: string; content?: string; url?: string }>;
}

// Fixed question for custom procedures
const FIXED_QUESTION =
  "Document the steps performed in the procedure and how we have obtained reasonable assurance based on the procedure performed.";

/**
 * Generate question ID for custom procedure
 * Format: <workspace prefix>_<custom procedure name>_001
 */
function generateQuestionId(workspacePrefix: string, procedureName: string): string {
  // Normalize procedure name: remove special characters, replace spaces with underscores
  const normalizedName = procedureName
    .replace(/[^a-zA-Z0-9\s]/g, "")
    .replace(/\s+/g, "_")
    .toUpperCase();
  return `${workspacePrefix}_${normalizedName}_001`;
}

export default function CustomProcedure({
  workspaceName,
  procedureName,
  workspacePrefix,
  area,
  onBack,
}: CustomProcedureProps) {
  const { isArchivalMode } = useArchivalMode();
  const [companyName, setCompanyName] = useState<string>("");
  const [questionResponse, setQuestionResponse] = useState<QuestionResponse>({});
  const [saving, setSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState<"idle" | "saving" | "saved" | "error">("idle");
  const [saveError, setSaveError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [sectionKey, setSectionKey] = useState<string>("");
  const [actualPhase, setActualPhase] = useState<string>("Execution");
  const [reviewCommentQuestionId, setReviewCommentQuestionId] = useState<string | null>(null);
  const documentsRef = useRef<Array<{ id: string; type: string; name: string; content?: string; url?: string }>>([]);

  const questionId = generateQuestionId(workspacePrefix, procedureName);

  // Load phase from Sections.json and generate sectionKey
  useEffect(() => {
    const loadPhaseAndSectionKey = async () => {
      try {
        if ((window as any).sharePointAPI?.downloadSectionsJson) {
          const result = await (window as any).sharePointAPI.downloadSectionsJson();
          if (result?.success && result?.data) {
            // Check which phase contains this workspace
            let phase = "Execution"; // Default
            const sectionsData = result.data;
            
            // Check Planning phase
            if (sectionsData.Planning && sectionsData.Planning[workspaceName]) {
              phase = "Planning";
            }
            // Check Execution phase
            else if (sectionsData.Execution && sectionsData.Execution[workspaceName]) {
              phase = "Execution";
            }
            // Check ConcludingProcedures phase
            else if (sectionsData.ConcludingProcedures && sectionsData.ConcludingProcedures[workspaceName]) {
              phase = "ConcludingProcedures";
            }
            
            setActualPhase(phase);
            
            // Generate sectionKey in format DocumentManager expects
            // Format: <phase-lowercase>-substantive:<workspaceName>
            const phaseLower = phase.toLowerCase();
            const sectionKeyValue = `${phaseLower}-substantive:${workspaceName}`;
            setSectionKey(sectionKeyValue);
          } else if (result?.error) {
            // Handle SharePoint API errors gracefully
            console.warn("Failed to load phase from Sections.json:", result.error);
            // Fallback: use area prop
            const phase = area === "ConcludingProcedures" ? "ConcludingProcedures" : area;
            setActualPhase(phase);
            const phaseLower = phase.toLowerCase();
            const sectionKeyValue = `${phaseLower}-substantive:${workspaceName}`;
            setSectionKey(sectionKeyValue);
          } else {
            // Fallback: use area prop
            const phase = area === "ConcludingProcedures" ? "ConcludingProcedures" : area;
            setActualPhase(phase);
            const phaseLower = phase.toLowerCase();
            const sectionKeyValue = `${phaseLower}-substantive:${workspaceName}`;
            setSectionKey(sectionKeyValue);
          }
        } else {
          // Fallback: use area prop
          const phase = area === "ConcludingProcedures" ? "ConcludingProcedures" : area;
          setActualPhase(phase);
          const phaseLower = phase.toLowerCase();
          const sectionKeyValue = `${phaseLower}-substantive:${workspaceName}`;
          setSectionKey(sectionKeyValue);
        }
      } catch (error) {
        console.warn("Failed to load phase from Sections.json, using fallback", error);
        // Fallback: use area prop
        const phase = area === "ConcludingProcedures" ? "ConcludingProcedures" : area;
        setActualPhase(phase);
        const phaseLower = phase.toLowerCase();
        const sectionKeyValue = `${phaseLower}-substantive:${workspaceName}`;
        setSectionKey(sectionKeyValue);
      }
    };
    
    loadPhaseAndSectionKey();
  }, [workspaceName, area]);

  // Load company name
  useEffect(() => {
    const loadCompanyName = async () => {
      try {
        const name = await getCompanyName("combined");
        setCompanyName(name || "ABC Private Limited");

      } catch (error) {
        console.warn("Failed to load company name, using fallback");
        setCompanyName("ABC Private Limited");
      }
    };
    loadCompanyName();
  }, []);

  // Load saved responses from SharePoint
  useEffect(() => {
    const loadSavedResponses = async () => {
      if (!companyName || !workspacePrefix) {
        setLoading(false);
        return;
      }

      try {
        const fileName = `Execution_${workspacePrefix}_Responses.json`;
        const result = await (window as any).sharePointAPI?.downloadClientJson?.(fileName);

        if (result?.success && result?.data?.substantiveProcedures) {
          const savedData = result.data.substantiveProcedures[questionId];
          if (savedData) {
            // Clear localStorage for this questionId to ensure SharePoint data takes priority
            localStorage.removeItem(`textbox_${questionId}`);
            localStorage.removeItem(`documents_${questionId}`);
            
            // Load textResponse from SharePoint
            let textResponse = null;
            if (savedData.textResponse) {
              textResponse = {
                html: savedData.textResponse.html || savedData.textResponse.plainText || '',
                plainText: savedData.textResponse.plainText || (savedData.textResponse.html ? savedData.textResponse.html.replace(/<[^>]*>/g, '') : '')
              };
              
              // Save to localStorage so DocumentManager can use it (but SharePoint is the source of truth)
              if (textResponse.html) {
                localStorage.setItem(`textbox_${questionId}`, textResponse.html);
              }
            }
            
            // Load documents from SharePoint
            const documents = (savedData.documents || []).map((docName: string) => ({
              id: `doc_${docName}`,
              type: "linked" as const,
              name: docName,
            }));
            
            // Update ref
            documentsRef.current = documents;
            
            // Save to localStorage so DocumentManager can use it (but SharePoint is the source of truth)
            localStorage.setItem(`documents_${questionId}`, JSON.stringify(documents));
            
            setQuestionResponse({
              textResponse,
              documents,
            });
          } else {
            // No saved data - clear localStorage to ensure clean state
            localStorage.removeItem(`textbox_${questionId}`);
            localStorage.removeItem(`documents_${questionId}`);
          }
          } else if (result?.error) {
            // Handle SharePoint API errors - log but continue with empty data
            console.warn(`Failed to load responses from SharePoint:`, result.error);
            // Clear localStorage if download failed
            localStorage.removeItem(`textbox_${questionId}`);
            localStorage.removeItem(`documents_${questionId}`);
            documentsRef.current = [];
          } else {
            // No file or no data - clear localStorage to ensure clean state
            localStorage.removeItem(`textbox_${questionId}`);
            localStorage.removeItem(`documents_${questionId}`);
            documentsRef.current = [];
          }
      } catch (error) {
        console.error("Failed to load saved responses:", error);
      } finally {
        setLoading(false);
      }
    };

    loadSavedResponses();
  }, [companyName, workspacePrefix, questionId]);

  const handleTextResponseChange = (textResponse: { html: string; plainText: string } | null) => {
    setQuestionResponse((prev) => ({
      ...prev,
      textResponse,
    }));
  };

  const handleDocumentsChange = (
    documents: Array<{ id: string; type: string; name: string; content?: string; url?: string }>
  ) => {
    // Update ref immediately for save function
    documentsRef.current = documents;
    setQuestionResponse((prev) => ({
      ...prev,
      documents,
    }));
  };

  const handleSave = useCallback(async () => {
    if (!companyName) {
      setSaveError("Company name is required");
      return;
    }

    setSaving(true);
    setSaveStatus("saving");
    setSaveError(null);

    try {
      // Use the correct file format: <phase name>_<workspace prefix>_Responses.json
      // Format phase name properly (Execution, Planning, or ConcludingProcedures)
      const phaseFormatted = actualPhase === 'ConcludingProcedures' ? 'ConcludingProcedures' 
        : actualPhase.charAt(0).toUpperCase() + actualPhase.slice(1).toLowerCase();
      const fileName = `${phaseFormatted}_${workspacePrefix}_Responses.json`;
      
      console.log(`[CustomProcedure] Saving to file: ${fileName} for questionId: ${questionId}`);
      
      // Auto-save any pending textbox content before processing responses
      // This matches the pattern used in other substantive procedure components
      const documentManagers = document.querySelectorAll('[data-document-manager]');
      for (const manager of Array.from(documentManagers)) {
        const itemId = manager.getAttribute('data-item-id');
        if (itemId) {
          const autoSaveMethod = (window as any)[`autoSaveTextbox_${itemId}`];
          if (autoSaveMethod && typeof autoSaveMethod === 'function') {
            autoSaveMethod();
          }
        }
      }

      // Wait for auto-save to complete
      // DocumentManager saves to localStorage with 50ms delay, and calls onDocumentsChange with 1000ms delay
      // So we wait 1500ms to ensure everything is saved
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      // Load existing file first
      let existingData: any = {};
      try {
        const downloadResult = await (window as any).sharePointAPI?.downloadClientJson?.(fileName);
        if (downloadResult?.success && downloadResult?.data) {
          existingData = downloadResult.data;
        } else if (downloadResult?.error) {
          // Handle SharePoint API errors - log but continue with empty data
          console.warn(`Failed to download existing ${fileName}:`, downloadResult.error);
        }
      } catch (error: any) {
        console.warn(`Failed to download existing ${fileName}, starting from empty`, error);
      }

      if (!existingData || typeof existingData !== "object" || Array.isArray(existingData)) {
        existingData = {};
      }

      if (!existingData.substantiveProcedures) {
        existingData.substantiveProcedures = {};
      }

      // Get the state for this question (from questionResponse)
      const state = questionResponse || { documents: [] };
      
      // Check localStorage for textbox content (highest priority - ensures latest content)
      const textboxFromLocalStorage = localStorage.getItem(`textbox_${questionId}`);
      const hasTextboxContent = textboxFromLocalStorage && textboxFromLocalStorage.trim().length > 0;
      
      // Construct section name to query db.json: <phase name>_<workspace prefix>_<question id>
      // Format phase name properly (Execution, Planning, or ConcludingProcedures)
      const sectionToFind = `${phaseFormatted}_${workspacePrefix}_${questionId}`;
      console.log(`[CustomProcedure] Querying db.json for section: ${sectionToFind}`);
      
      // Query db.json to find all files with this section in their "section" field
      // The section format is: <phase name>_<workspace prefix>_<question id>
      let documentNames: string[] = [];
      try {
        if ((window as any).sharePointAPI?.loadDocumentListBySection) {
          const dbResult = await (window as any).sharePointAPI.loadDocumentListBySection(sectionToFind);
          if (dbResult?.success && dbResult?.data) {
            // The result.data is an array of file objects with a "name" field
            if (Array.isArray(dbResult.data)) {
              documentNames = dbResult.data
                .map((file: any) => file.name || '')
                .filter((name: string) => name && name.trim().length > 0);
              console.log(`[CustomProcedure] Found ${documentNames.length} files in db.json for section ${sectionToFind}:`, documentNames);
            }
          } else if (dbResult?.error) {
            console.warn(`[CustomProcedure] Error querying db.json for section ${sectionToFind}:`, dbResult.error);
          } else {
            console.log(`[CustomProcedure] No files found in db.json for section ${sectionToFind}`);
          }
        } else {
          console.warn(`[CustomProcedure] loadDocumentListBySection API not available`);
        }
      } catch (error) {
        console.error(`[CustomProcedure] Failed to query db.json for section ${sectionToFind}:`, error);
      }
      
      console.log(`[CustomProcedure] Final document names to save:`, documentNames);
      
      // Build textResponse - localStorage takes priority, then state
      let textResponse = { html: "", plainText: "" };
      if (hasTextboxContent) {
        textResponse = {
          html: textboxFromLocalStorage,
          plainText: textboxFromLocalStorage.replace(/<[^>]*>/g, '') // Strip HTML tags
        };
      } else if (state.textResponse) {
        textResponse = state.textResponse;
      }
      
      console.log(`[CustomProcedure] Saving ${documentNames.length} documents for ${questionId}:`, documentNames);

      // Save to the question ID in substantiveProcedures - same structure as other components
      existingData.substantiveProcedures[questionId] = {
        selectedOption: "",
        textResponse: textResponse,
        documents: documentNames, // Document names are saved here
        table: [],
        lastUpdated: new Date().toISOString(),
        companyName,
      };
      
      console.log(`[CustomProcedure] Saved data for ${questionId}:`, {
        textResponse: !!textResponse.html,
        documentsCount: documentNames.length,
        documents: documentNames
      });

      // Upload to SharePoint
      const jsonString = JSON.stringify(existingData, null, 2);
      const base64Content = btoa(unescape(encodeURIComponent(jsonString)));

      // Get FY_YEAR from database dynamically
      const fyYear = getFY_YEAR();
      if (!fyYear) {
        throw new Error("FY_YEAR is not configured. Please ensure you have selected an engagement.");
      }

      const uploadResult = await (window as any).sharePointAPI?.uploadFile?.({
        fileContent: base64Content,
        fileName,
        referenceValue: `${phaseFormatted} ${workspacePrefix} Substantive Responses`,
        folderName: "juggernaut",
        fyYear: fyYear,
        sectionList: [`${phaseFormatted}_${workspacePrefix}_Responses`],
      });

      if (!uploadResult || !uploadResult.success) {
        const errorMessage = uploadResult?.error || "Failed to save responses";
        // Provide more user-friendly error message for common SharePoint errors
        if (errorMessage.includes("Failed to get site ID")) {
          throw new Error("Unable to connect to SharePoint. Please check your connection and try again.");
        }
        throw new Error(errorMessage);
      }

      setSaveStatus("saved");
      setTimeout(() => setSaveStatus("idle"), 3000);
    } catch (error) {
      console.error("Failed to save responses:", error);
      setSaveStatus("error");
      setSaveError(error instanceof Error ? error.message : "Failed to save responses");
    } finally {
      setSaving(false);
    }
  }, [companyName, workspacePrefix, questionId, questionResponse, actualPhase]);

  if (loading || !sectionKey) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-white/60" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-white">{procedureName}</h2>
          <p className="text-gray-400">{workspaceName} - Custom Procedure</p>
        </div>
        <div className="flex items-center gap-2">
          {saveStatus === "saved" && (
            <Badge className="bg-green-500/20 text-green-400 border-green-500/30">
              Saved
            </Badge>
          )}
          {saveStatus === "error" && (
            <Badge className="bg-red-500/20 text-red-400 border-red-500/30">Error</Badge>
          )}
          {onBack && (
            <Button variant="outline" onClick={onBack}>
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back
            </Button>
          )}
        </div>
      </div>

      {/* Question Card */}
      <Card className="border-white/10 bg-white/5 text-white">
        <CardHeader>
          <div className="flex items-start justify-between gap-2">
            <CardTitle className="text-lg flex-1">{FIXED_QUESTION}</CardTitle>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => setReviewCommentQuestionId(questionId)}
              className="flex-shrink-0 p-1 h-7 w-7 text-yellow-400 hover:text-yellow-300 hover:bg-white/10"
              title="Review Comment"
            >
              <MessageSquare className="h-4 w-4" />
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <DocumentManager
            sectionKey={sectionKey}
            groupKey="custom-procedure"
            itemId={questionId}
            itemQuestion={FIXED_QUESTION}
            itemTextResponse={questionResponse.textResponse}
            itemDocuments={questionResponse.documents || []}
            onDocumentsChange={handleDocumentsChange}
            onTextResponseChange={handleTextResponseChange}
            disabled={isArchivalMode}
          />
        </CardContent>
      </Card>

      {/* Save Button */}
      <div className="flex justify-end">
        <Button
          onClick={handleSave}
          disabled={saving || isArchivalMode}
          className="min-w-[120px]"
        >
          {saving ? (
            <>
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              Saving...
            </>
          ) : (
            <>
              <Save className="h-4 w-4 mr-2" />
              Save
            </>
          )}
        </Button>
      </div>

      {saveError && (
        <div className="rounded-lg border border-red-500/30 bg-red-500/10 p-4 text-red-400">
          {saveError}
        </div>
      )}

      {/* Review Comment Modal */}
      {reviewCommentQuestionId && (
        <ReviewComment
          questionId={reviewCommentQuestionId}
          questionText={FIXED_QUESTION}
          isOpen={!!reviewCommentQuestionId}
          onClose={() => setReviewCommentQuestionId(null)}
        />
      )}
    </div>
  );
}

